// ─────────────────────────────────────────────────────────────
//  AppointmentPasteDetector
//
//  When the user pastes an SMS/email that looks like an appointment
//  reminder into the chat composer, surface a subtle banner that
//  offers to extract and save the appointment.
//
//  Flow: banner → extract (LLM) → editable modal → save.
//  Never auto-saves. Always user-confirmed.
//
//  Integration: parent calls `<AppointmentPasteDetector pastedText={t}/>`
//  after capturing an onPaste event; parent is responsible for debounce
//  and clearing `pastedText` once the component fires `onResolved`.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useState, useRef } from 'react';
import axios, { API } from '../../services/api';
import { track } from '../../services/activation';
import { X, Sparkle, CalendarBlank, Clock, MapPin, User, Stethoscope, Spinner, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Fast client-side heuristic — mirror of backend _looks_like_appointment.
// Catches the obvious cases so we only ping the backend for real candidates.
const APPT_KW = /\b(appointment|appt|booking|booked|confirm|reminder|reschedul|consult|clinic|dr\.?\s|dr\s|gp|medical centre|hospital|pathology|radiology|mri|scan|ultrasound|blood test|ecg)\b/i;
const DATE_LIKE = /\b(\d{1,2}[\s\/\-.](?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)|(?:mon|tue|wed|thu|fri|sat|sun)\w*|\d{1,2}[\s\/\-.]\d{1,2}(?:[\s\/\-.]\d{2,4})?|tomorrow|today)\b/i;
const TIME_LIKE = /\b\d{1,2}(?:[:\.]\d{2})?\s?(?:am|pm)\b|\b\d{1,2}:\d{2}\b/i;

export function looksLikeAppointment(text) {
  if (!text || text.trim().length < 15) return false;
  return APPT_KW.test(text) && (DATE_LIKE.test(text) || TIME_LIKE.test(text));
}

const Field = ({ label, value, onChange, placeholder, Icon, testid }) => (
  <div className="space-y-1">
    <label className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
      {Icon && <Icon size={12} />}{label}
    </label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#235347] focus:outline-none focus:ring-1 focus:ring-[#235347]/20"
      data-testid={testid}
    />
  </div>
);

export default function AppointmentPasteDetector({ pastedText, onResolved }) {
  const [stage, setStage] = useState('idle'); // idle | banner | parsing | review | saving
  const [parsed, setParsed] = useState(null);
  const [draft, setDraft] = useState(null);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!pastedText) { setStage('idle'); resolvedRef.current = false; return; }
    if (looksLikeAppointment(pastedText)) {
      setStage('banner');
      resolvedRef.current = false;
    } else {
      setStage('idle');
    }
  }, [pastedText]);

  const resolve = () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    onResolved?.();
  };

  const dismiss = (reason = 'user') => {
    track('paste_appointment_dismissed', { reason });
    setStage('idle');
    setParsed(null);
    setDraft(null);
    resolve();
  };

  const handleExtract = async () => {
    setStage('parsing');
    try {
      const { data } = await axios.post(`${API}/appointments/parse-text`, { text: pastedText });
      if (!data?.is_appointment || !data.parsed) {
        toast.info("I couldn't find enough details — try adding manually.");
        dismiss('low_confidence');
        return;
      }
      track('paste_appointment_detected', {
        confidence: data.confidence,
        has_date: !!data.parsed.date,
        has_time: !!data.parsed.time,
        has_clinician: !!data.parsed.clinician_name,
      });
      setParsed(data.parsed);
      setDraft({ ...data.parsed });
      setStage('review');
    } catch {
      toast.error('Could not read the appointment. Please add it manually.');
      dismiss('error');
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    setStage('saving');
    try {
      const payload = {
        title: draft.title || 'Appointment',
        date: draft.date || '',
        time: draft.time || '',
        clinician_name: draft.clinician_name || undefined,
        clinic_name: draft.clinic_name || undefined,
        specialty: draft.specialty || undefined,
        purpose: draft.purpose || undefined,
        location: draft.location || undefined,
      };
      const { data } = await axios.post(`${API}/appointments`, payload);
      track('paste_appointment_saved', {
        appointment_id: data?.id,
        has_location: !!payload.location,
        has_clinician: !!payload.clinician_name,
      });
      // Quality signal: give the user a one-tap "looked right?" chip on the
      // success toast. Fires once per save, only if they actively confirm.
      const apptId = data?.id;
      toast.success("I've saved that for you. I'll help you prepare.", {
        duration: 8000,
        action: {
          label: 'Looked right',
          onClick: () => {
            track('paste_appointment_satisfied', { appointment_id: apptId });
          },
        },
      });
      setStage('idle');
      setParsed(null);
      setDraft(null);
      resolve();
    } catch {
      toast.error('Could not save the appointment.');
      setStage('review');
    }
  };

  if (stage === 'idle') return null;

  // ── Banner (pre-extract) ──
  if (stage === 'banner' || stage === 'parsing') {
    return (
      <div
        className="mb-2 bg-[#235347]/5 border border-[#235347]/20 rounded-xl px-3 py-2.5 flex items-center gap-3"
        data-testid="appt-paste-banner"
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white flex items-center justify-center">
          <Sparkle size={16} className="text-[#235347]" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#235347]" data-testid="appt-paste-banner-title">
            Looks like an appointment
          </p>
          <p className="text-[11px] text-[#235347]/70 truncate">
            Want me to save it and help you prepare?
          </p>
        </div>
        <button
          onClick={handleExtract}
          disabled={stage === 'parsing'}
          className="bg-[#235347] text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-[#1A3F36] disabled:opacity-60 min-h-[32px] flex items-center gap-1.5"
          data-testid="appt-paste-extract-btn"
        >
          {stage === 'parsing' ? (<><Spinner size={12} className="animate-spin" /> Reading…</>) : 'Review'}
        </button>
        <button
          onClick={() => dismiss('user')}
          className="text-slate-400 hover:text-slate-600 p-1"
          aria-label="Dismiss"
          data-testid="appt-paste-dismiss-btn"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // ── Review modal (editable) ──
  if (stage === 'review' || stage === 'saving') {
    return (
      <div
        className="fixed inset-0 z-[200] bg-black/40 flex items-end sm:items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) dismiss('modal_backdrop'); }}
        data-testid="appt-paste-modal"
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3.5 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#235347] font-semibold">Review appointment</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Edit anything before I save it.</p>
            </div>
            <button
              onClick={() => dismiss('modal_close')}
              disabled={stage === 'saving'}
              className="text-slate-400 hover:text-slate-600 p-1"
              aria-label="Close"
              data-testid="appt-paste-modal-close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} placeholder="e.g. GP consult" testid="appt-paste-field-title" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date" value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} placeholder="YYYY-MM-DD" Icon={CalendarBlank} testid="appt-paste-field-date" />
              <Field label="Time" value={draft.time} onChange={(v) => setDraft({ ...draft, time: v })} placeholder="14:30" Icon={Clock} testid="appt-paste-field-time" />
            </div>
            <Field label="Clinician" value={draft.clinician_name} onChange={(v) => setDraft({ ...draft, clinician_name: v })} placeholder="Dr Jane Smith" Icon={User} testid="appt-paste-field-clinician" />
            <Field label="Clinic" value={draft.clinic_name} onChange={(v) => setDraft({ ...draft, clinic_name: v })} placeholder="Medical centre name" Icon={Stethoscope} testid="appt-paste-field-clinic" />
            <Field label="Location" value={draft.location} onChange={(v) => setDraft({ ...draft, location: v })} placeholder="Address or venue" Icon={MapPin} testid="appt-paste-field-location" />
            <Field label="Purpose" value={draft.purpose} onChange={(v) => setDraft({ ...draft, purpose: v })} placeholder="Follow-up, review, etc." testid="appt-paste-field-purpose" />
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex items-center justify-end gap-2">
            <button
              onClick={() => dismiss('modal_cancel')}
              disabled={stage === 'saving'}
              className="text-slate-500 hover:text-slate-700 text-sm font-medium px-3 py-2 min-h-[40px]"
              data-testid="appt-paste-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={stage === 'saving' || !(draft.date || draft.time) || !draft.title}
              className="bg-[#235347] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#1A3F36] disabled:opacity-60 min-h-[40px] flex items-center gap-1.5"
              data-testid="appt-paste-save-btn"
            >
              {stage === 'saving' ? (<><Spinner size={14} className="animate-spin" /> Saving…</>) : (<><CheckCircle size={14} weight="fill" /> Save appointment</>)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
